import { LitElement, html } from 'lit';
import styles from './studio-tagger.css.mjs';
import '../studio-icon/studio-icon.mjs';
import '../studio-tooltip/studio-tooltip.mjs';

export class StudioTagger extends LitElement {
  static styles = styles;

  static properties = {
    tagList: { type: Array },
    selectedTags: { type: Array },
    relatedTags: { type: Array },
    inputValue: { type: String },
    filteredSuggestions: { type: Array },
    minChars: { type: Number },
    highlightedIndex: { type: Number },
    apiDebounceTime: { type: Number },
    disabled: { type: Boolean, reflect: true },
    font: { type: String },
    color: { type: String },

    mode: { type: String, reflect: true }, // 'generic' | 'publication' | 'asset'
    labels: { type: Object },
    allowQuickAdd: { type: Boolean },
    context: { type: Object },
    strategy: { type: Object },
    attachOnAdd: { type: Boolean },
    attachOnRemove: { type: Boolean },
  };

  constructor() {
    super();
    this.tagList = [];
    this.selectedTags = [];
    this.relatedTags = [];
    this.inputValue = '';
    this.filteredSuggestions = [];
    this.minChars = 2;
    this.highlightedIndex = -1;
    this.apiDebounceTime = 300;
    this.disabled = false;
    this.font = '';
    this.color = '';

    this.mode = 'generic';
    this.labels = {};
    this.allowQuickAdd = true;
    this.context = {};

		// - strategy: {
		//     fetchSuggestions?(client, query, context): Promise<Tag[]>,
		//     onAdd?(client, tag, context): Promise<void>,
		//     onRemove?(client, tag, context): Promise<void>,
		//     buildNewTag?(input, context): Tag
		//   }
    this.strategy = null;
    this.attachOnAdd = false;
    this.attachOnRemove = false;

    this._debounceTimer = null;
    this._client = null;
  }

  connectedCallback() {
    super.connectedCallback();
    this._resolveClient();
    this.addEventListener('keydown', this._handleKeyDown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this._handleKeyDown);
  }

  async _resolveClient() {
    const getClientEvent = new CustomEvent('get-tag-client', {
      detail: {
        callback: (client) => (this._client = client),
      },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(getClientEvent);
  }

  updated(changed) {
    if (changed.has('inputValue')) this._filterSuggestions();
  }

  _handleInput(e) {
    if (this.disabled) return;
    this.inputValue = e.target.value;
    clearTimeout(this._debounceTimer);
    if (this.inputValue.length >= this.minChars) {
      this._debounceTimer = setTimeout(() => {
        this._fetchSuggestions(this.inputValue);
      }, this.apiDebounceTime);
    } else {
      this.filteredSuggestions = [];
    }
  }

  async _fetchSuggestions(query) {
    if (!query) return;

  	this._resolveClient();

    // 1) Let host intercept via event and provide a result Promise
    const ev = new CustomEvent('before-fetch-suggestions', {
      detail: { query, context: this.context, result: null },
      bubbles: true,
      composed: true,
      cancelable: true,
    });
    const notCanceled = this.dispatchEvent(ev);
    if (!notCanceled && ev.detail.result) {
      try {
        const items = await ev.detail.result;
        this.tagList = Array.isArray(items) ? items : [];
        this._filterSuggestions();
      } catch (err) {
        console.error('[studio-tagger] before-fetch-suggestions failed:', err);
      }
      return;
    }

    // 2) Strategy override
    if (this.strategy?.fetchSuggestions && this._client) {
      try {
        const items = await this.strategy.fetchSuggestions(this._client, query, this.context);
        this.tagList = Array.isArray(items) ? items : [];
        this._filterSuggestions();
        return;
      } catch (err) {
        console.error('[studio-tagger] strategy.fetchSuggestions failed:', err);
        // fall through to default
      }
    }

    // 3) Default to client.listTags
    if (!this._client || !this._client.listTags) return;
    try {
      const res = await this._client.listTags({ query });
      this.tagList = Array.isArray(res?.items) ? res.items : [];
      this._filterSuggestions();
    } catch (err) {
      console.error('[studio-tagger] Failed to fetch suggestions:', err);
    }
  }

  _filterSuggestions() {
    const query = this.inputValue?.toLowerCase() || '';
    this.filteredSuggestions = this.tagList.filter(
      (tag) =>
        query &&
        tag?.displayName?.toLowerCase().includes(query) &&
        !this.selectedTags.some((t) => t.id === tag.id)
    );
    this.highlightedIndex = -1;
  }

  _handleKeyDown = (e) => {
    if (this.disabled) return;
    const len = this.filteredSuggestions.length;
    if (e.key === 'Escape') {
      this.filteredSuggestions = [];
    } else if (e.key === 'ArrowDown' && len > 0) {
      e.preventDefault();
      this.highlightedIndex = (this.highlightedIndex + 1) % len;
    } else if (e.key === 'ArrowUp' && len > 0) {
      e.preventDefault();
      this.highlightedIndex = (this.highlightedIndex - 1 + len) % len;
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (this.highlightedIndex >= 0) {
        this._addTag(this.filteredSuggestions[this.highlightedIndex]);
      } else {
        this._handleQuickAdd();
      }
    }
  };

  _handleQuickAdd() {
    if (this.disabled || !this.allowQuickAdd) return;
    const val = this.inputValue.trim();
    if (!val) return;

    const base = { id: val.toLowerCase(), displayName: val };
    const newTag =
      (this.strategy?.buildNewTag && (this.strategy.buildNewTag(val, this.context) || base)) ||
      base;

    this._addTag(newTag);
  }

  async _addTag(tag) {
    if (this.disabled || !tag || this.selectedTags.find((t) => t.id === tag.id)) return;

    // Let host intercept and handle add
    const ev = new CustomEvent('before-add-tag', {
      detail: { tag, context: this.context },
      bubbles: true,
      composed: true,
      cancelable: true,
    });
    const notCanceled = this.dispatchEvent(ev);
    if (!notCanceled) return;

    // Local UI state
    this.selectedTags = [...this.selectedTags, tag];
    this.inputValue = '';
    this.filteredSuggestions = [];

    // Optional side-effect
    try {
      if (this.strategy?.onAdd && this._client) {
        await this.strategy.onAdd(this._client, tag, this.context);
      } else if (
        this.attachOnAdd &&
        this.context?.targetUri &&
        this._client?.attachTag &&
        typeof this._client.attachTag === 'function'
      ) {
        await this._client.attachTag(tag.id, { uri: this.context.targetUri });
      }
    } catch (err) {
      console.error('[studio-tagger] onAdd failed:', err);
    }

    this._emitChange('added', tag);
  }

  async _removeTag(tag) {
    if (this.disabled || !tag) return;

    const index = this.selectedTags.findIndex((t) => t.id === tag.id);
    if (index === -1) return;

    // Let host intercept and handle remove
    const ev = new CustomEvent('before-remove-tag', {
      detail: { tag, context: this.context },
      bubbles: true,
      composed: true,
      cancelable: true,
    });
    const notCanceled = this.dispatchEvent(ev);
    if (!notCanceled) return;

    const finalize = async () => {
      this.selectedTags = this.selectedTags.filter((t) => t.id !== tag.id);
      try {
        if (this.strategy?.onRemove && this._client) {
          await this.strategy.onRemove(this._client, tag, this.context);
        } else if (
          this.attachOnRemove &&
          this.context?.targetUri &&
          this._client?.detachTag &&
          typeof this._client.detachTag === 'function'
        ) {
          await this._client.detachTag(tag.id, { uri: this.context.targetUri });
        }
      } catch (err) {
        console.error('[studio-tagger] onRemove failed:', err);
      }
      this._emitChange('removed', tag);
    };

    // Animate if possible
    const tagEl = this.shadowRoot?.querySelectorAll('.selected-tags .tag-pill')[index];
    if (tagEl) {
      tagEl.classList.add('removing');
      setTimeout(finalize, 180); // matches CSS duration
    } else {
      await finalize();
    }
  }

  _emitChange(action, tag) {
    this.dispatchEvent(
      new CustomEvent('tags-changed', {
        detail: { action, tag, selectedTags: this.selectedTags },
        bubbles: true,
        composed: true,
      })
    );
  }

  // Public helper for “attach after publish” flows
  async attachSelected({ client = this._client, targetUri } = {}) {
    if (!client?.attachTag || !targetUri || !Array.isArray(this.selectedTags)) return;
    for (const t of this.selectedTags) {
      try {
        await client.attachTag(t.id, { uri: targetUri });
      } catch (err) {
        console.error('[studio-tagger] attachSelected failed:', err);
      }
    }
  }

  render() {
    const publicationTitle =
      this.labels.publicationTitle ??
      (this.mode === 'publication' ? 'Publication Tags' : 'Selected Tags');
    const relatedTitle = this.labels.relatedTitle ?? 'Related Content Tags';
    const placeholder = this.labels.inputPlaceholder ?? 'Search for tags...';

		const publicationTooltip =
      this.labels.publicationTooltip ??
      (this.mode === 'publication'
        ? 'Publication tags are associated with the publication.'
        : 'Tags currently selected.');
    const relatedTooltip =
      this.labels.relatedTooltip ?? 'Related Content tags are associated with the content.';

    return html`
      <div class="tag-selector-container" role="application">
        <div class="input-row">
          <input
            class="tag-input"
            type="text"
            .value=${this.inputValue}
            placeholder=${placeholder}
            @input=${this._handleInput}
            aria-label="Tag input"
            role="combobox"
            aria-autocomplete="list"
            aria-expanded=${!!this.filteredSuggestions.length}
            ?disabled=${this.disabled}
          />
        </div>

        ${this.filteredSuggestions.length
          ? html`
              <div class="suggestions" role="listbox">
                ${this.filteredSuggestions.map(
                  (tag, index) => html`
                    <div
                      class="suggestion ${index === this.highlightedIndex ? 'highlighted' : ''}"
                      role="option"
                      @click=${() => this._addTag(tag)}
                      aria-selected=${index === this.highlightedIndex}
                    >
                      ${tag.displayName} <studio-icon name="square-plus"></studio-icon>
                    </div>
                  `
                )}
              </div>
            `
          : ''}

        <hr />

        <div class="title-row">
          <studio-text variant="button-large">${publicationTitle}</studio-text>
          <studio-tooltip
            position="right"
						text=${publicationTooltip}
						trigger="click"
          >
            <studio-icon 
							name="circle-info" 
							color="var(--color-gray-4, #7C7C7C)"
							></studio-icon>
          </studio-tooltip>
        </div>

        <div class="publication-section">
          ${this.selectedTags.length
            ? html`
                <div class="selected-tags">
                  ${this.selectedTags.map(
                    (tag) => html`
                      <div class="tag-pill" @click=${() => this._removeTag(tag)}>
                        ${tag.displayName}
                        <studio-icon name="square-minus"></studio-icon>
                      </div>
                    `
                  )}
                </div>
              `
            : html `<div class="no-selected-tags">No tags added yet<div>`}
        </div>

        ${this.relatedTags.length
          ? html`
              <div class="title-row">
                <studio-text variant="button-large">${relatedTitle}</studio-text>
                <studio-tooltip
                  position="right"
									text=${relatedTooltip}
									trigger="click"
                >
                	<studio-icon 
										name="circle-info" 
										color="var(--color-gray-4, #7C7C7C)"
									>
									</studio-icon>
                </studio-tooltip>
              </div>
              <div class="suggestions related-suggestions">
                ${this.relatedTags.map((tag) => {
                  const alreadyAdded = this.selectedTags.some((t) => t.id === tag.id);
                  return html`
                    <div
                      class="suggestion ${alreadyAdded ? 'added' : ''}"
                      role="option"
                      @click=${alreadyAdded ? null : () => this._addTag(tag)}
                    >
                      ${tag.displayName}
                      ${alreadyAdded
                        ? html`<studio-icon name="check"></studio-icon>`
                        : html`<studio-icon name="circle-plus"></studio-icon>`}
                    </div>
                  `;
                })}
              </div>
            `
          : ''}
      </div>
    `;
  }
}

customElements.define('studio-tagger', StudioTagger);
